import * as React from "react";
import * as moment from "moment";
import { Button, ButtonGroup, Grid,  List, ListItem, Menu, Modal, Tab } from "semantic-ui-react";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { FileUpload } from "@teamprevent/common/fileUploads";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { ApplicationState } from "../../../../applicationState";
import { routeIdSelector as customerIdSelector } from "../../../customer/selectors";
import { createOhCaseDocumentSuccess, deleteOhCaseDocument, loadOhCaseDocuments, updateOhCaseDocumentSuccess } from "../../actions";
import { OhCaseDocument, OhCaseDocumentVisibilityOptions } from "../../model";
import { routeIdSelector as ohCaseIdSelector } from "../../selectors";

import { useOhCaseDetailsModel } from "../../hooks";

import { DocumentsLabel } from "./DocumentsTabLabel";
import { DocumentVisibilitySettingsModal } from "./DocumentVisibilitySettingsModal";

const documentTypes = [
    { name: "Clinical Notes" },
    { name: "Form" },
    { name: "General" },
    { name: "Report" }
];

const Documents: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const ohCaseId = useSelector(ohCaseIdSelector);
    const customerId = useSelector(customerIdSelector);
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const { ohCase } = useOhCaseDetailsModel({ ohCaseId, customerId });

    const [fileUploadKey, setFileUploadKey] = React.useState<number>(0);
    const [newFileModalOpen, setNewFileModalOpen] = React.useState<boolean>(false);
    const [visibilitySettingsModalOpen, setVisibilitySettingsModalOpen] = React.useState<boolean>(false);
    const [documentToUpdate, setDocumentToUpdate] = React.useState<OhCaseDocument | undefined>(undefined);

    const documents: OhCaseDocument[] = React.useMemo(() => {
        return (ohCase?.documents ?? []).sort((d1, d2) => -d1.dateCreated!.diff(d2.dateCreated));
    }, [ohCase?.documents]);

    React.useEffect(() => {
        dispatch(loadOhCaseDocuments({ customerId, ohCaseId }));
    }, [customerId, dispatch, ohCaseId]);

    const mapOhCaseDocumentVisibilityDisplay = (documentType: string | undefined): string => {
        switch (documentType) {
            case OhCaseDocumentVisibilityOptions.Admin:
                return "Admin Only";
            case OhCaseDocumentVisibilityOptions.Employee:
            case OhCaseDocumentVisibilityOptions.Manager:
                return documentType;
            case OhCaseDocumentVisibilityOptions.EmployeeAndManager:
                return "Manager | Employee";
            default:
                return "";
        }
    };

    const documentCanSetVisibility = (documentType: string): boolean => documentType === "Form" || documentType === "Report";

    const deleteDocument = React.useCallback((ohCaseDocumentId: string) => {
        dispatch(deleteOhCaseDocument(customerId, ohCaseId, ohCaseDocumentId));
    }, [customerId, dispatch, ohCaseId]);

    const documentUploadedCallback = React.useCallback((ohCaseDocumentId: string, fileName: string, documentType: string) => {
        const newDocumentModel: OhCaseDocument = {
            id: ohCaseDocumentId,
            name: fileName,
            documentType,
            visibleTo: OhCaseDocumentVisibilityOptions.Admin, // Don't really like this here in case backend changes and we no loner default to Admin
            dateCreated: moment.utc(),
            completedBy: {
                id: currentUser.id!,
                name: currentUser.name
            }
        };

        dispatch(createOhCaseDocumentSuccess({ ohCaseId, document: newDocumentModel }));
        setFileUploadKey((prevValue) => prevValue + 1);

        if (documentCanSetVisibility(documentType)) {
            setDocumentToUpdate(newDocumentModel);
            setVisibilitySettingsModalOpen(true);
        }
        else {
            setNewFileModalOpen(false);
        }

    }, [currentUser, dispatch, ohCaseId]);

    const openNewFileModal = React.useCallback(() => setNewFileModalOpen(true), []);
    const closeNewFileModal = React.useCallback(() => setNewFileModalOpen(false), []);

    const closeVisibilitySettingsModal = React.useCallback(() => {
        setVisibilitySettingsModalOpen(false);
        setNewFileModalOpen(false);
        setDocumentToUpdate(undefined);
    }, []);

    const documentVisibilityChanged = React.useCallback((selectedVisibility: keyof typeof OhCaseDocumentVisibilityOptions) => {
        if (documentToUpdate) {

            documentToUpdate.visibleTo = selectedVisibility;
            dispatch(updateOhCaseDocumentSuccess({ ohCaseId, document: documentToUpdate }));
        }

        setDocumentToUpdate(undefined);
        setVisibilitySettingsModalOpen(false);
    }, [dispatch, ohCaseId, documentToUpdate]);

    const onDocumentEdit = React.useCallback((document: OhCaseDocument) => {
        setDocumentToUpdate(document);
        setVisibilitySettingsModalOpen(true);
    }, []);

    const renderButtons = (document: OhCaseDocument) => {
        const deleteDocumentButton = (fluid: boolean) => (
            <ConfirmedButton
                icon="delete"
                className="warning"
                content="Delete"
                floated="right"
                fluid={fluid}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => deleteDocument(document.id)}
                promptText="Are you sure you want to delete this document? Type 'DELETE' and click confirm to delete."
                typeToConfirmText="DELETE"
                headingText="Delete Document"
            />
        );

        const editDocumentButton = (
            <Button
                icon="pencil"
                content="Edit"
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => onDocumentEdit(document)}
            />
        );

        return (
            <>
                {(documentCanSetVisibility(document.documentType)) &&
                    <ButtonGroup floated="right" fluid>
                        {editDocumentButton}
                        {deleteDocumentButton(false)}
                    </ButtonGroup>
                }
                {!(documentCanSetVisibility(document.documentType)) &&
                    deleteDocumentButton(true)
                }
            </>
        );
    };

    return (
        <>
            <Grid columns="equal">
                <Grid.Row textAlign="center">
                    <Grid.Column width={6}>
                        <Button
                            size="large"
                            fluid
                            content="Add New Document"
                            onClick={openNewFileModal}
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={4} textAlign="center">
                        <strong>Document Name</strong>
                    </Grid.Column>
                    <Grid.Column width={2} verticalAlign="middle">
                        <strong>Document Type</strong>
                    </Grid.Column>
                    <Grid.Column width={2} verticalAlign="middle">
                        <strong>Date Uploaded</strong>
                    </Grid.Column>
                    <Grid.Column width={3} verticalAlign="middle">
                        <strong>Uploaded By</strong>
                    </Grid.Column>
                    <Grid.Column width={2} verticalAlign="middle">
                        <strong>Visible To</strong>
                    </Grid.Column>
                    <Grid.Column width={3} textAlign="center">
                        <strong>Action</strong>
                    </Grid.Column>
                </Grid.Row>
                {documents.map((document, index) => {
                    const downloadUrl =
                        `/api/customer/${customerId}/case/${ohCaseId}/document/${document.id}?fileName=${document.name}`;
                    return (
                        <Grid.Row key={index}>
                            <Grid.Column width={4} verticalAlign="middle">
                                <a href={downloadUrl}>
                                    <Button content={document.name} type="button" fluid />
                                </a>
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="middle">
                                {document.documentType}
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="middle">
                                <span title={document.dateCreated?.toString()}>
                                    {document.dateCreated && document.dateCreated.local().format("DD/MM/yyyy HH:mm")}
                                </span>
                            </Grid.Column>
                            <Grid.Column width={3} verticalAlign="middle">
                                {document.completedBy?.name}
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="middle">
                                {mapOhCaseDocumentVisibilityDisplay(document.visibleTo)}
                            </Grid.Column>
                            <Grid.Column width={3} verticalAlign="middle">
                                {renderButtons(document)}
                            </Grid.Column>
                        </Grid.Row>
                    );
                })}
            </Grid>
            <Modal open={newFileModalOpen}>
                <Modal.Header>
                            Upload Case Document
                </Modal.Header>
                <Modal.Content>
                    <List horizontal>
                        {documentTypes.map(t =>
                            (<ListItem key={`${t.name}-${fileUploadKey}`}>
                                <FileUpload
                                    apiUrl={`/api/customer/${customerId}/case/${ohCaseId}/document?documentType=${encodeURIComponent(t.name)}`}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onFileChange={(documentId, fileName) => documentUploadedCallback(documentId, fileName, t.name)}
                                    label={t.name}
                                    acceptExtensions={acceptableExtensions}
                                />
                            </ListItem>)
                        )}
                    </List>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        content="Cancel"
                        onClick={closeNewFileModal}
                    />
                </Modal.Actions>
            </Modal>
            <DocumentVisibilitySettingsModal
                modalOpen={visibilitySettingsModalOpen}
                customerId={customerId}
                ohCaseId={ohCaseId}
                ohCaseDocument={documentToUpdate}
                onDocumentVisibilityChange={documentVisibilityChanged}
                onCloseVisibilitySettingsModal={closeVisibilitySettingsModal}
                genericCaseType={ohCase?.genericCaseType ?? null}
            />
        </>
    );
};

export const documentsTab = (customerId: string, ohCaseId: string) => ({
    path: "/documents",
    menuItem: (
        <Menu.Item key='documents'>
            Documents <DocumentsLabel customerId={customerId} ohCaseId={ ohCaseId } />
        </Menu.Item>
    ),
    render: () => <Tab.Pane><Documents /></Tab.Pane>
});
